import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Star, Shield, TrendingUp } from 'lucide-react';

interface UserStats {
  user_id: string;
  total_ratings: number;
  average_rating: number;
  total_exchanges: number;
  trust_score: number;
  badges: string;
}

interface UserStatsCardProps {
  userId: string;
  userName: string;
  compact?: boolean;
  hideTrustScore?: boolean;
}

const UserStatsCard: React.FC<UserStatsCardProps> = ({ userId, userName, compact = false, hideTrustScore = false }) => {
  const [stats, setStats] = useState<UserStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUserStats();
  }, [userId]);

  const fetchUserStats = async () => {
    try {
      const response = await axios.get(`/api/ratings/user/${userId}/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching user stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTrustLevel = (score: number) => {
    if (score >= 85) return { 
      text: 'Très fiable', 
      color: 'text-green-600', 
      bg: 'bg-green-50',
      description: 'Utilisateur avec excellente réputation'
    };
    if (score >= 70) return { 
      text: 'Fiable', 
      color: 'text-forest-700', 
      bg: 'bg-forest-50',
      description: 'Utilisateur avec bonne réputation'
    };
    if (score >= 50) return { 
      text: 'En développement', 
      color: 'text-yellow-600', 
      bg: 'bg-yellow-50',
      description: 'Utilisateur en développement de sa réputation'
    };
    return { 
      text: 'Nouveau membre', 
      color: 'text-gray-600', 
      bg: 'bg-gray-50',
      description: 'Nouveau membre de la communauté'
    };
  };

  const getBadgeInfo = (badge: string) => {
    const badgeConfig = {
      'nouveau': { icon: TrendingUp, color: 'bg-forest-100 text-forest-800', text: 'Nouveau' },
      'fiable': { icon: Shield, color: 'bg-green-100 text-green-800', text: 'Fiable' },
      'expert': { icon: Shield, color: 'bg-purple-100 text-purple-800', text: 'Expert' },
      'excellent': { icon: Star, color: 'bg-yellow-100 text-yellow-800', text: 'Excellent' },
      'très-bien': { icon: Star, color: 'bg-orange-100 text-orange-800', text: 'Très bien' },
      'bien': { icon: Star, color: 'bg-forest-100 text-forest-800', text: 'Bien' },
      'confiance-or': { icon: Shield, color: 'bg-yellow-100 text-yellow-800', text: 'Confiance Or' },
      'confiance-argent': { icon: Shield, color: 'bg-gray-100 text-gray-800', text: 'Confiance Argent' },
      'confiance-bronze': { icon: Shield, color: 'bg-orange-100 text-orange-800', text: 'Confiance Bronze' }
    };
    return badgeConfig[badge as keyof typeof badgeConfig];
  };

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
        <div className="h-3 bg-gray-200 rounded w-1/2"></div>
      </div>
    );
  }

  if (!stats) {
    return null;
  }

  const trustLevel = getTrustLevel(stats.trust_score);
  const badges = stats.badges ? stats.badges.split(',').filter(Boolean) : [];

  if (compact) {
    return (
      <div className="inline-flex items-center space-x-3 text-sm">
        <div className="flex items-center space-x-1">
          <Star className={`h-4 w-4 ${stats.total_ratings >= 5 ? 'text-yellow-400 fill-current' : 'text-gray-300'}`} />
          {stats.total_ratings >= 5 ? (
            <>
              <span className="font-medium">{stats.average_rating % 1 === 0 ? stats.average_rating : stats.average_rating.toFixed(1)}</span>
              <span className="text-gray-500">({stats.total_ratings})</span>
            </>
          ) : (
            <>
              <span className="text-gray-400 text-xs">Pas assez d'avis</span>
              <span className="text-gray-400 text-xs">({stats.total_ratings})</span>
            </>
          )}
        </div>
        <div 
          className={`px-2 py-1 rounded-full text-xs font-medium ${trustLevel.bg} ${trustLevel.color} cursor-help`}
          title={trustLevel.description}
        >
          {trustLevel.text}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-medium text-gray-900">Réputation de {userName}</h4>
        <div className={`px-2 py-1 rounded-full text-xs font-medium ${trustLevel.bg} ${trustLevel.color}`}>
          {trustLevel.text}
        </div>
      </div>

      <div className="space-y-3">
        {/* Rating - Only show if user has at least 5 ratings */}
        {stats.total_ratings >= 5 ? (
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Star className="h-4 w-4 text-yellow-400 fill-current" />
              <span className="text-sm text-gray-600">Note moyenne</span>
            </div>
            <div className="text-right">
              <span className="font-medium">{stats.average_rating % 1 === 0 ? stats.average_rating : stats.average_rating.toFixed(1)}/5</span>
              <span className="text-xs text-gray-500 ml-1">({stats.total_ratings} avis)</span>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Star className="h-4 w-4 text-gray-300" />
              <span className="text-sm text-gray-500">Note moyenne</span>
            </div>
            <div className="text-right">
              <span className="text-sm text-gray-400">Pas assez d'avis</span>
              <span className="text-xs text-gray-400 ml-1">({stats.total_ratings} avis)</span>
            </div>
          </div>
        )}

        {/* Trust Score */}
        {!hideTrustScore && (
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Shield className="h-4 w-4 text-forest-500" />
              <span className="text-sm text-gray-600">Score de confiance</span>
            </div>
            <span className="font-medium text-forest-700">{stats.trust_score}/100</span>
          </div>
        )}

        {/* Exchanges */}
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">Échanges effectués</span>
          <span className="font-medium">{stats.total_exchanges}</span>
        </div>

        {/* Badges */}
        {badges.length > 0 && (
          <div className="pt-2 border-t border-gray-100">
            <div className="flex flex-wrap gap-1">
              {badges.slice(0, 3).map((badge) => {
                const badgeInfo = getBadgeInfo(badge);
                if (!badgeInfo) return null;
                
                const Icon = badgeInfo.icon;
                return (
                  <span
                    key={badge}
                    className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${badgeInfo.color}`}
                  >
                    <Icon className="h-3 w-3 mr-1" />
                    {badgeInfo.text}
                  </span>
                );
              })}
              {badges.length > 3 && (
                <span className="text-xs text-gray-500 px-2 py-1">
                  +{badges.length - 3} autres
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserStatsCard; 